import React, { useCallback, useState } from 'react';
import { IonButton, IonIcon, IonPopover, isPlatform } from '@ionic/react';
import { ellipsisHorizontal, ellipsisVertical } from 'ionicons/icons';

interface OverflowMenuControls {
  close: () => void;
  menuItemStyle: React.CSSProperties;
}

interface OverflowMenuProps {
  ariaLabel: string;
  buttonId?: string;
  buttonTestId?: string;
  buttonStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  children: (controls: OverflowMenuControls) => React.ReactNode;
}

const defaultContentStyle: React.CSSProperties = {
  minWidth: '180px',
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const defaultButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: '-8px',
  top: '50%',
  transform: 'translateY(-50%)',
  margin: 0,
};

const defaultMenuItemStyle: React.CSSProperties = {
  width: '100%',
  margin: 0,
  textAlign: 'left',
};

const overflowMenuIcon = isPlatform('android')
  ? ellipsisVertical
  : ellipsisHorizontal;

const OverflowMenu: React.FC<OverflowMenuProps> = ({
  ariaLabel,
  buttonId,
  buttonTestId,
  buttonStyle,
  contentStyle,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverEvent, setPopoverEvent] = useState<MouseEvent>();

  const close = useCallback(() => {
    setIsOpen(false);
    setPopoverEvent(undefined);
  }, []);

  return (
    <>
      <IonButton
        id={buttonId}
        fill="clear"
        aria-label={ariaLabel}
        data-testid={buttonTestId}
        onClick={event => {
          setPopoverEvent(event.nativeEvent);
          setIsOpen(true);
        }}
        style={{ ...defaultButtonStyle, ...buttonStyle }}
      >
        <IonIcon icon={overflowMenuIcon} slot="icon-only" />
      </IonButton>
      <IonPopover
        isOpen={isOpen}
        event={popoverEvent}
        onDidDismiss={close}
        side="bottom"
        alignment="end"
      >
        <div style={{ ...defaultContentStyle, ...contentStyle }}>
          {children({ close, menuItemStyle: defaultMenuItemStyle })}
        </div>
      </IonPopover>
    </>
  );
};

export default OverflowMenu;
